import React, { useState, useEffect } from 'react';
import { ArrowRight, ArrowUpRight, Menu, X, Cloud, Smartphone, Database, Code, GitBranch, Award, Users, FileText, BookOpen, Download } from 'lucide-react';

const Portfolio = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('featured');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const smoothScroll = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const projects = [
    {
      id: 1,
      number: '01',
      title: 'Multi-Cloud Infrastructure Automation',
      description: 'Architected enterprise-grade infrastructure across AWS, Azure, and GCP using Terraform. Implemented automated deployment pipelines, monitoring, and cost optimization strategies resulting in 15% cost reduction.',
      tech: ['AWS', 'Azure', 'GCP', 'Terraform', 'Jenkins', 'CloudWatch'],
      category: 'cloud',
      highlights: ['Multi-cloud deployment', 'Infrastructure as Code', 'Cost optimization'],
      impact: '15% cost reduction, 99.9% uptime',
      featured: true
    },
    {
      id: 2,
      number: '02',
      title: 'Aurora Tracker - Cross-Platform Mobile App',
      description: 'Built a real-time aurora tracking application for iOS and Android using React Native. Integrated AI-powered assistant, push notifications, and location-based features. Deployed on App Store and Google Play.',
      tech: ['React Native', 'Node.js', 'OpenAI API', 'Firebase', 'Redux'],
      category: 'mobile',
      highlights: ['Cross-platform development', 'AI integration', 'Real-time data'],
      impact: '25% user engagement increase',
      featured: true
    },
    {
      id: 3,
      number: '03',
      title: 'Financial Services Platform - TCS',
      description: 'Led development of scalable RESTful APIs for real-time financial transactions serving 1M+ users. Implemented microservices architecture with advanced caching and security features.',
      tech: ['Java', 'Spring Boot', 'MySQL', 'Redis', 'AWS EC2', 'Docker'],
      category: 'fullstack',
      highlights: ['Microservices', 'High-scale system', 'Financial compliance'],
      impact: '30% efficiency improvement, 50% faster response time',
      featured: true
    },
    {
      id: 4,
      number: '04',
      title: 'Social Network Mental Disorders Detection',
      description: 'ML-based application to predict social mental disorders using various algorithms and user interaction patterns. Implemented KNN algorithm, Naive Bayes classification, and Random Forest prediction with Windows GUI application.',
      tech: ['Python', 'Machine Learning', 'KNN', 'Tkinter', 'Random Forest'],
      category: 'datascience',
      highlights: ['Machine Learning', 'Mental Health Tech', 'Research Publication'],
      impact: 'Published in IJRASET 2023',
      featured: true,
      publication: {
        title: 'Social Network Mental Disorders Detection',
        authors: 'Nashikkar, S., Hanamar, A., Pathan, N., & Mulla, H.',
        journal: 'International Journal for Research in Applied Science & Engineering Technology (IJRASET)',
        year: '2023',
        volume: '11(IX)',
        link: 'https://www.ijraset.com/research-paper/social-network-mental-disorders-detection'
      }
    },
    {
      id: 5,
      number: '05',
      title: 'Healthcare SaaS Platform',
      description: 'Developed HIPAA-compliant healthcare platform with 70+ APIs. Built modular microservices architecture ensuring data security and 99.9% uptime for critical medical systems.',
      tech: ['.NET Core', 'React.js', 'PostgreSQL', 'Azure', 'Next.js'],
      category: 'fullstack',
      highlights: ['HIPAA compliance', 'Microservices', 'Healthcare tech'],
      impact: '99.9% uptime, Zero security incidents'
    },
    {
      id: 6,
      number: '06',
      title: 'Recession Prediction System',
      description: 'Machine Learning-based system for predicting economic recession trends using SVM and Linear Regression. Achieved 86% prediction accuracy with inflation rate analysis, yield curve prediction, and interactive visualization.',
      tech: ['Python', 'SVM', 'Linear Regression', 'Matplotlib', 'Tkinter'],
      category: 'datascience',
      highlights: ['Economic Analysis', 'ML Predictions', 'Research Publication'],
      impact: '86% prediction accuracy',
      publication: {
        title: 'Recession Prediction using SVM and Linear Regression',
        authors: 'Nashikkar, S., Hanamar, A., Pathan, N., & Mulla, H.',
        journal: 'International Journal for Research in Applied Science & Engineering Technology',
        year: '2023',
        volume: '11(IX)',
        link: 'https://www.ijraset.com/research-paper/recession-prediction'
      }
    },
    {
      id: 7,
      number: '07',
      title: 'Traffic Signal Synchronization and Dynamic Control',
      description: 'Intelligent traffic management system using dynamic signal synchronization algorithms. Reduced traffic congestion by 35% through real-time traffic flow analysis and adaptive signal timing.',
      tech: ['Python', 'Computer Vision', 'IoT', 'Machine Learning', 'OpenCV'],
      category: 'datascience',
      highlights: ['Smart City Tech', 'Real-time Processing', 'Research Publication'],
      impact: '35% reduction in traffic congestion',
      publication: {
        title: 'Traffic Signal Synchronization and Dynamic Control',
        authors: 'Hanamar, A., et al.',
        journal: 'International Journal for Research in Applied Science & Engineering Technology',
        year: '2023',
        link: 'https://www.ijraset.com/research-paper/traffic-signal-synchronization-and-dynamic-control'
      }
    },
    {
      id: 8,
      number: '08',
      title: 'Customer Analytics Dashboard',
      description: 'Built data science solution for customer behavior analysis using Python and machine learning. Created interactive dashboards for real-time insights and predictive analytics.',
      tech: ['Python', 'Pandas', 'NumPy', 'Scikit-learn', 'Tableau', 'AWS S3'],
      category: 'datascience',
      highlights: ['Machine Learning', 'Data visualization', 'Predictive analytics'],
      impact: '40% improvement in customer retention predictions'
    },
    {
      id: 9,
      number: '09',
      title: 'Cloud Migration & DevOps Pipeline',
      description: 'Migrated legacy monolithic applications to cloud-native architecture. Implemented CI/CD pipelines with automated testing, monitoring, and rollback capabilities.',
      tech: ['GitHub Actions', 'Docker', 'Kubernetes', 'AWS', 'SonarQube'],
      category: 'cloud',
      highlights: ['Cloud migration', 'DevOps automation', 'Container orchestration'],
      impact: '40% faster deployment, 60% reduction in downtime'
    },
    {
      id: 10,
      number: '10',
      title: 'Finance Management Mobile App',
      description: 'Created personal finance tracking application with native features for iOS and Android. Implemented secure authentication, real-time syncing, and data visualization.',
      tech: ['React Native', 'Node.js', 'MongoDB', 'Chart.js', 'JWT'],
      category: 'mobile',
      highlights: ['Native features', 'Secure authentication', 'Real-time sync'],
      impact: 'Featured in finance app category'
    },
    {
      id: 11,
      number: '11',
      title: 'Sales Forecast ML Model',
      description: 'Developed machine learning models for sales forecasting using historical data. Implemented time series analysis and feature engineering for accurate predictions.',
      tech: ['Python', 'TensorFlow', 'Pandas', 'AWS SageMaker', 'Jupyter'],
      category: 'datascience',
      highlights: ['Time series analysis', 'Feature engineering', 'ML deployment'],
      impact: '85% prediction accuracy'
    },
    {
      id: 12,
      number: '12',
      title: 'E-commerce Platform',
      description: 'Full-stack e-commerce solution with payment integration, inventory management, and real-time order tracking. Built with microservices architecture for scalability.',
      tech: ['React', 'Node.js', 'Stripe API', 'PostgreSQL', 'Redis'],
      category: 'fullstack',
      highlights: ['Payment Integration', 'Scalable Architecture', 'Real-time Updates'],
      impact: 'Processing 10K+ orders monthly'
    }
  ];

  const filterProjects = (category) => {
    setActiveFilter(category);
  };

  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : activeFilter === 'featured'
    ? projects.filter(project => project.featured)
    : projects.filter(project => project.category === activeFilter);

  const featuredProjects = projects.filter(project => project.featured);

  return (
    <div className="min-h-screen bg-white text-black overflow-x-hidden" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
      {/* Grid Overlay */}
      <div 
        className="fixed inset-0 pointer-events-none z-0" 
        style={{
          opacity: 0.03,
          backgroundImage: `
            linear-gradient(#000 1px, transparent 1px),
            linear-gradient(90deg, #000 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }} 
      />

      {/* Navigation */}
      <nav className={`fixed top-0 w-full bg-white z-50 transition-all duration-300 border-b ${
        scrolled ? 'py-4 bg-white/95 backdrop-blur-sm' : 'py-8 border-transparent'
      }`} style={{ borderColor: scrolled ? '#e5e5e5' : 'transparent' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="flex justify-between items-center">
            <button onClick={() => smoothScroll('home')} className="text-2xl font-bold hover:opacity-70 transition-opacity" style={{ letterSpacing: '-0.05em' }}>AH.</button>
            
            {/* Desktop Menu */}
            <ul className="hidden md:flex gap-12 list-none items-center">
              {['Home', 'Projects', 'About', 'Contact'].map((item) => (
                <li key={item}>
                  <button
                    onClick={() => smoothScroll(item.toLowerCase())}
                    className="text-gray-600 hover:text-black transition-colors font-medium relative group"
                  >
                    {item}
                    <span className="absolute -bottom-2 left-0 w-0 h-0.5 bg-black transition-all group-hover:w-full" />
                  </button>
                </li>
              ))}
              {/* Resume Download Link - Desktop */}
              <li>
                <a
                  href="../Aryaa_Hanamar_Resume.pdf"
                  download="Aryaa_Hanamar_Resume.pdf"
                  className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-black border border-gray-300 hover:border-black transition-all font-medium"
                >
                  <Download size={16} />
                  Resume
                </a>
              </li>
            </ul>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 -mr-2 hover:opacity-70 transition-opacity"
              aria-label="Menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          <div className={`md:hidden absolute top-full left-0 w-full bg-white border-t border-gray-200 py-8 shadow-lg transition-all duration-300 ${
            isMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
          }`}>
              <ul className="list-none space-y-6 px-6 md:px-8">
                {['Home', 'Projects', 'About', 'Contact'].map((item) => (
                  <li key={item}>
                    <button
                      onClick={() => smoothScroll(item.toLowerCase())}
                      className="text-lg text-gray-600 hover:text-black transition-colors font-medium block w-full text-left"
                    >
                      {item}
                    </button>
                  </li>
                ))}
                {/* Resume Download Link - Mobile */}
                <li>
                  <a
                    href="/resume.pdf"
                    download="Aryaa_Hanamar_Resume.pdf"
                    className="flex items-center gap-2 text-lg text-gray-600 hover:text-black transition-colors font-medium"
                  >
                    <Download size={20} />
                    Download Resume
                  </a>
                </li>
              </ul>
            </div>
          </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="min-h-screen flex items-center px-6 md:px-8 relative">
        {/* Floating elements */}
        <div 
          className="absolute top-1/5 right-1/10 w-52 h-52 border-2 border-black opacity-10"
          style={{
            animation: 'float 20s infinite ease-in-out',
            top: '20%',
            right: '10%'
          }}
        />
        <div 
          className="absolute bottom-1/5 left-1/20 w-36 h-36 bg-black rounded-full opacity-10"
          style={{
            animation: 'floatReverse 25s infinite ease-in-out',
            bottom: '20%',
            left: '5%'
          }}
        />

        <div className="max-w-7xl mx-auto w-full">
          <p 
            className="text-sm font-semibold uppercase text-gray-500 mb-8"
            style={{ letterSpacing: '0.2em', animation: 'fadeInUp 0.8s ease forwards' }}
          >
            Full Stack Developer
          </p>
          <h1 
            className="font-bold mb-8"
            style={{ 
              fontSize: 'clamp(3rem, 8vw, 6rem)',
              lineHeight: 0.9,
              letterSpacing: '-0.05em',
              animation: 'fadeInUp 0.8s ease 0.2s forwards',
              opacity: 0,
              animationFillMode: 'forwards'
            }}
          >
            ARYAA<br />
            <span style={{
              WebkitTextStroke: '2px black',
              WebkitTextFillColor: 'transparent',
              display: 'block'
            }}>HANAMAR</span>
          </h1>
          <p 
            className="text-lg md:text-xl text-gray-600 max-w-2xl mb-8 md:mb-12 font-light"
            style={{ 
              animation: 'fadeInUp 0.8s ease 0.4s forwards',
              opacity: 0,
              animationFillMode: 'forwards'
            }}
          >
            I craft elegant digital solutions with modern technologies. Currently pursuing 
            my Master's at Northeastern University while building scalable applications 
            that make a difference.
          </p>
          <div 
            className="flex flex-wrap gap-4"
            style={{ 
              animation: 'fadeInUp 0.8s ease 0.6s forwards',
              opacity: 0,
              animationFillMode: 'forwards'
            }}
          >
            <button
              onClick={() => smoothScroll('projects')}
              className="px-8 md:px-10 py-4 bg-black text-white font-semibold border-2 border-black hover:translate-x-1 transition-transform"
            >
              View Projects
            </button>
            <button
              onClick={() => smoothScroll('contact')}
              className="px-8 md:px-10 py-4 bg-transparent text-black font-semibold border-2 border-black hover:bg-black hover:text-white transition-all"
            >
              Get In Touch
            </button>
          </div>
        </div>
      </section>

  {/* About Section */}
      <section id="about" className="py-20 md:py-32 px-6 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <p className="text-sm font-semibold uppercase text-gray-500 mb-4" style={{ letterSpacing: '0.2em' }}>
              01 / ABOUT
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-12 md:gap-24">
            <div className="space-y-12">
              <div className="border-b border-gray-300 pb-8">
                <div className="text-4xl md:text-5xl font-bold mb-2">3+</div>
                <div className="text-gray-600 uppercase text-sm" style={{ letterSpacing: '0.1em' }}>Years Experience</div>
              </div>
              <div className="border-b border-gray-300 pb-8">
                <div className="text-4xl md:text-5xl font-bold mb-2">30%</div>
                <div className="text-gray-600 uppercase text-sm" style={{ letterSpacing: '0.1em' }}>Efficiency Improved</div>
              </div>
              <div className="border-b border-gray-300 pb-8">
                <div className="text-4xl md:text-5xl font-bold mb-2">7</div>
                <div className="text-gray-600 uppercase text-sm" style={{ letterSpacing: '0.1em' }}>Team Members Led</div>
              </div>
            </div>
            
            <div className="lg:col-span-2">
              <h2 className="font-semibold mb-8" style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', letterSpacing: '-0.03em' }}>
                Building the future, one line at a time
              </h2>
              <div className="space-y-6 text-gray-600">
                <p>
                  I'm a Full Stack Developer with a passion for creating innovative solutions 
                  that bridge the gap between elegant design and robust functionality. With over 
                  3 years of experience at Tata Consultancy Services, I've specialized in building 
                  scalable applications for the financial sector.
                </p>
                <p>
                  Currently pursuing my Master's in Software Engineering Systems at Northeastern 
                  University, I'm expanding my expertise in cloud computing and modern web technologies. 
                  I believe in writing clean, maintainable code that not only solves problems but 
                  also creates delightful user experiences.
                </p>
                <p>
                  When I'm not coding, you'll find me exploring new technologies, contributing to 
                  open-source projects, or mentoring junior developers. I'm always excited about 
                  tackling challenging problems and turning ideas into reality.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

    

      {/* All Projects Section */}
      <section id="projects" className="py-20 md:py-32 px-6 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-20">
            <p className="text-sm font-semibold uppercase text-gray-500 mb-4" style={{ letterSpacing: '0.2em' }}>
              02 / PORTFOLIO
            </p>
            <h2 className="font-semibold mb-8" style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', letterSpacing: '-0.03em' }}>
              All Projects
            </h2>
            
            {/* Filter Buttons */}
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'featured', label: 'Featured Projects' },
                { value: 'all', label: 'All Projects' },
                { value: 'cloud', label: 'Cloud & DevOps', icon: Cloud },
                { value: 'fullstack', label: 'Full Stack', icon: Code },
                { value: 'mobile', label: 'Mobile Apps', icon: Smartphone },
                { value: 'datascience', label: 'Data Science', icon: Database }
              ].map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => filterProjects(filter.value)}
                  className={`px-4 py-2 font-medium border-2 transition-all flex items-center gap-2 ${
                    activeFilter === filter.value 
                      ? 'bg-black text-white border-black' 
                      : 'bg-white text-black border-gray-300 hover:border-black'
                  }`}
                >
                  {filter.icon && <filter.icon size={16} />}
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-16">
            {filteredProjects.map((project, index) => (
              <div key={project.id} className={`grid md:grid-cols-2 gap-8 md:gap-16 items-center py-12 ${index > 0 ? 'border-t border-gray-300' : ''}`}>
                <div className="relative h-64 md:h-96 bg-gray-200 overflow-hidden group transition-transform duration-500 hover:scale-[0.98]">
                  <span 
                    className="absolute top-6 left-6 md:top-8 md:left-8 text-6xl md:text-8xl font-bold text-white"
                    style={{ mixBlendMode: 'difference' }}
                  >
                    {project.number}
                  </span>
                  
                  {/* Category Badge */}
                  <span className="absolute top-6 right-6 px-3 py-1 bg-black text-white text-xs uppercase font-medium">
                    {project.category}
                  </span>
                  
                  {/* Publication Badge */}
                  {project.publication && (
                    <span className="absolute top-16 right-6 px-3 py-1 bg-white text-black border border-black text-xs uppercase font-medium flex items-center gap-1">
                      <BookOpen size={14} /> Published
                    </span>
                  )}
                </div>
                <div>
                  <h3 className="text-2xl md:text-3xl font-semibold mb-4">{project.title}</h3>
                  <p className="text-gray-600 mb-6">{project.description}</p>
                  
                  {/* Highlights */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {project.highlights.map((highlight, idx) => (
                      <span key={idx} className="text-sm text-gray-500">
                        {highlight} {idx < project.highlights.length - 1 && '•'}
                      </span>
                    ))}
                  </div>
                  
                  {/* Publication Info */}
                  {project.publication && (
                    <div className="mb-6 p-4 bg-gray-100 rounded-lg">
                      <p className="text-sm font-semibold mb-2 flex items-center gap-2">
                        <FileText size={16} /> Research Publication
                      </p>
                      <p className="text-sm text-gray-700 mb-2">
                        {project.publication.authors} ({project.publication.year}). <em>{project.publication.title}</em>. 
                        {project.publication.journal} {project.publication.volume && `, ${project.publication.volume}`}
                      </p>
                      <a 
                        href={project.publication.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        View Publication <ArrowUpRight size={14} />
                      </a>
                    </div>
                  )}
                  
                  {/* Impact */}
                  <p className="text-sm font-semibold text-green-600 mb-6">
                    Impact: {project.impact}
                  </p>
                  
                  <div className="flex flex-wrap gap-2 md:gap-4 mb-8">
                    {project.tech.map((tech, techIndex) => (
                      <span key={techIndex} className="px-4 py-2 bg-black text-white text-sm font-medium">
                        {tech}
                      </span>
                    ))}
                  </div>
                  <button className="inline-flex items-center gap-2 font-semibold hover:gap-4 transition-all">
                    View Details <ArrowRight size={20} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="py-20 md:py-32 px-6 md:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="mb-20">
            <p className="text-sm font-semibold uppercase text-gray-500 mb-4" style={{ letterSpacing: '0.2em' }}>
              03 / EXPERTISE
            </p>
            <h2 className="font-semibold" style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', letterSpacing: '-0.03em' }}>
              Technical Skills
            </h2>
          </div>

          {/* Skills Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            <div className="bg-white p-8 border border-gray-200 hover:border-black transition-all group">
              <Cloud className="mb-4 group-hover:text-gray-700" size={32} />
              <h3 className="text-xl font-semibold mb-4">Cloud & Multi-Cloud</h3>
              <p className="text-sm mb-4 text-gray-600">
                Expert in architecting and deploying solutions across multiple cloud platforms
              </p>
              <div className="flex flex-wrap gap-2">
                {['AWS', 'Azure', 'GCP', 'Terraform', 'CloudFormation', 'Docker', 'Kubernetes'].map((skill) => (
                  <span key={skill} className="text-xs px-2 py-1 bg-gray-100 text-black">
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            <div className="bg-white p-8 border border-gray-200 hover:border-black transition-all group">
              <Code className="mb-4 group-hover:text-gray-700" size={32} />
              <h3 className="text-xl font-semibold mb-4">Full Stack Development</h3>
              <p className="text-sm mb-4 text-gray-600">
                Building scalable applications from frontend to backend
              </p>
              <div className="flex flex-wrap gap-2">
                {['React', 'Node.js', 'Java', 'Spring Boot', '.NET Core', 'Python', 'TypeScript'].map((skill) => (
                  <span key={skill} className="text-xs px-2 py-1 bg-gray-100 text-black">
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            <div className="bg-white p-8 border border-gray-200 hover:border-black transition-all group">
              <Smartphone className="mb-4 group-hover:text-gray-700" size={32} />
              <h3 className="text-xl font-semibold mb-4">Mobile Development</h3>
              <p className="text-sm mb-4 text-gray-600">
                Creating cross-platform mobile experiences
              </p>
              <div className="flex flex-wrap gap-2">
                {['React Native', 'iOS', 'Android', 'Firebase', 'Redux', 'Push Notifications'].map((skill) => (
                  <span key={skill} className="text-xs px-2 py-1 bg-gray-100 text-black">
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            <div className="bg-white p-8 border border-gray-200 hover:border-black transition-all group">
              <Database className="mb-4 group-hover:text-gray-700" size={32} />
              <h3 className="text-xl font-semibold mb-4">Data Science & ML</h3>
              <p className="text-sm mb-4 text-gray-600">
                Leveraging data for insights and predictions
              </p>
              <div className="flex flex-wrap gap-2">
                {['Python', 'Pandas', 'NumPy', 'TensorFlow', 'Scikit-learn', 'Jupyter', 'SageMaker'].map((skill) => (
                  <span key={skill} className="text-xs px-2 py-1 bg-gray-100 text-black">
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            <div className="bg-white p-8 border border-gray-200 hover:border-black transition-all group">
              <GitBranch className="mb-4 group-hover:text-gray-700" size={32} />
              <h3 className="text-xl font-semibold mb-4">DevOps & CI/CD</h3>
              <p className="text-sm mb-4 text-gray-600">
                Automating deployment and ensuring reliability
              </p>
              <div className="flex flex-wrap gap-2">
                {['Jenkins', 'GitHub Actions', 'SonarQube', 'JUnit', 'Monitoring', 'CloudWatch'].map((skill) => (
                  <span key={skill} className="text-xs px-2 py-1 bg-gray-100 text-black">
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            <div className="bg-white p-8 border border-gray-200 hover:border-black transition-all group">
              <Users className="mb-4 group-hover:text-gray-700" size={32} />
              <h3 className="text-xl font-semibold mb-4">Leadership & Soft Skills</h3>
              <p className="text-sm mb-4 text-gray-600">
                Leading teams and driving results
              </p>
              <div className="flex flex-wrap gap-2">
                {['Team Leadership', 'Agile/Scrum', 'Code Reviews', 'Mentoring', 'Communication'].map((skill) => (
                  <span key={skill} className="text-xs px-2 py-1 bg-gray-100 text-black">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

    
      {/* Contact Section */}
      <section id="contact" className="py-20 md:py-32 px-6 md:px-8 bg-black text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <p className="text-sm font-semibold uppercase text-gray-400 mb-8" style={{ letterSpacing: '0.2em' }}>
              04 / CONTACT
            </p>
            <h2 className="font-semibold mb-8" style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', letterSpacing: '-0.03em' }}>
              Let's create something amazing together
            </h2>
            <p className="text-lg md:text-xl opacity-80">
              I'm always interested in hearing about new projects and opportunities.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-15 mt-20" style={{ textAlign: 'center' }}>
            <div>
              <p className="text-sm uppercase text-gray-400 mb-2" style={{ letterSpacing: '0.1em' }}>Email</p>
              <a href="mailto:hanamar.a@northeastern.edu" className="text-lg md:text-xl hover:opacity-70 transition-opacity break-all">
                hanamar.a@northeastern.edu
              </a>
            </div>
            <div>
              <p className="text-sm uppercase text-gray-400 mb-2" style={{ letterSpacing: '0.1em' }}>LinkedIn</p>
              <a href="https://linkedin.com/in/aryaa-hanamar" target="_blank" rel="noopener noreferrer" className="text-lg md:text-xl hover:opacity-70 transition-opacity inline-flex items-center gap-1">
                /in/aryaa-hanamar <ArrowUpRight size={16} />
              </a>
            </div>
            <div>
              <p className="text-sm uppercase text-gray-400 mb-2" style={{ letterSpacing: '0.1em' }}>GitHub</p>
              <a href="https://github.com/Aryaa-ah" target="_blank" rel="noopener noreferrer" className="text-lg md:text-xl hover:opacity-70 transition-opacity inline-flex items-center gap-1">
                @aryaa-ah <ArrowUpRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </section>

      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap');
        
        @keyframes float {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          33% { transform: translate(30px, -30px) rotate(120deg); }
          66% { transform: translate(-20px, 20px) rotate(240deg); }
        }

        @keyframes floatReverse {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          33% { transform: translate(-30px, -30px) rotate(-120deg); }
          66% { transform: translate(20px, 20px) rotate(-240deg); }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Portfolio;